import { useGetOffer } from '~/hooks/apis/offers/useGetOffer';
import { useGetTourOffer } from '~/hooks/apis/tours/useTourOffers';

import { Bundle, HotelBundleComponent, TourBundleComponent } from '~/types/bundles/bundle';

import { hotelOnSaleRegions, sharedRegions, tourScheduledRegions } from './regions';

/**
 * Where the bundle can sell: the regions its tour is scheduled in that its hotel is on sale in.
 * An approved bundle's tour is its LTLE, which has no schedules of its own, so its base tour's are used.
 */
export default function useBundleRegions(bundle: Bundle) {
  const tour = bundle.components.find(
    (component): component is TourBundleComponent => component.product_type === 'tour',
  );
  const hotel = bundle.components.find(
    (component): component is HotelBundleComponent => component.product_type === 'flashAccommodation',
  );

  const { data: tourOffer, isLoading: isLoadingTour, error: tourError } = useGetTourOffer(tour?.tour_offer_id);
  const baseTourOfferId = tourOffer?.services.find((service) => service.linkedTourOfferId)?.linkedTourOfferId;
  const {
    data: baseTourOffer,
    isLoading: isLoadingBase,
    error: baseError,
  } = useGetTourOffer(baseTourOfferId ?? undefined);
  const { data: hotelOffer, isLoading: isLoadingHotel, error: hotelError } = useGetOffer(hotel?.offer_id);

  const scheduledTour = baseTourOfferId ? baseTourOffer : tourOffer;
  const tourRegions = scheduledTour ? tourScheduledRegions(scheduledTour) : [];
  const hotelRegions = hotelOffer ? hotelOnSaleRegions(hotelOffer) : [];

  return {
    hasTour: !!tour,
    hasHotel: !!hotel,
    tourRegions,
    hotelRegions,
    shared: sharedRegions(tourRegions, hotelRegions),
    isLoading: (!!tour && (isLoadingTour || isLoadingBase)) || (!!hotel && isLoadingHotel),
    // A failed fetch leaves the regions unknown, not empty.
    error: tourError ?? baseError ?? hotelError,
  };
}
